// Symptom/headache active-day segmentation for the ItemReportModal Calendar
// overlay and Chart tab (ITEM_REPORT_ENHANCEMENTS_PLAN.md D-IE-14/D-IE-15).
// Answers "which days is this symptom active" — deliberately does NOT reuse
// itemReport.ts's episodeStartEvents(), which answers a different question
// ("which events start a new episode", used for Count-mode bar/badge totals).
// Pure, deterministic, client-side only — no I/O; events must already be
// item-scoped and filtered (see itemEvents()).

import type { MedicalEvent } from "./types.js";
import { localCalendarDate } from "./datetime.js";

/** `date` (YYYY-MM-DD) shifted by `offsetDays`, via the Date.UTC walk `chartDays`
 *  uses — never local Date construction, so DST never shifts a day boundary. */
function utcDayWalk(date: string, offsetDays: number): string {
  const [y, m, d] = date.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + offsetDays);
  return dt.toISOString().slice(0, 10);
}

/** D-IE-14 resolution sentinel: severity 0, or resolvedAt set (mirrors symptomStats.ts's isResolutionEvent). */
function isResolutionEvent(e: MedicalEvent): boolean {
  const p = e.payload as Record<string, unknown>;
  return (typeof p.severity === "number" && p.severity === 0) || typeof p.resolvedAt === "string";
}

export interface SymptomActiveSpan {
  startDate: string; // yyyy-mm-dd, inclusive — local date of the opening check-in
  endDate: string; // yyyy-mm-dd, inclusive
  peakSeverity: number; // max severity >= 1 within the span
  lastSeverity: number; // severity of the most recent check-in in the span
  ongoing: boolean; // true when the span was closed by "today" rather than by
  // a resolution event or the auto-close horizon
  resolved: boolean; // true when closed by an explicit resolution event
}

/** Per-day severity while active: date -> carried-forward severity (D-IE-15). */
export interface SymptomActivity {
  spans: SymptomActiveSpan[];
  severityByDay: Map<string, number>;
}

interface CheckIn {
  date: string;
  severity: number;
}

/**
 * Fill `severityByDay` for one span's check-ins, carrying each check-in's
 * severity forward across the days until the next check-in (or `fillThrough`
 * for the tail), inclusive of `fillThrough`.
 */
function fillSeverityByDay(
  severityByDay: Map<string, number>,
  checkIns: CheckIn[],
  fillThrough: string
): void {
  for (let i = 0; i < checkIns.length; i++) {
    const cur = checkIns[i];
    const nextDate = i + 1 < checkIns.length ? checkIns[i + 1].date : utcDayWalk(fillThrough, 1);
    let d = cur.date;
    while (d <= fillThrough && d < nextDate) {
      severityByDay.set(d, cur.severity);
      d = utcDayWalk(d, 1);
    }
  }
}

/**
 * Segment symptom/headache events into day-granular active spans.
 * `events` must be item-scoped (one symptom identity) and already filtered.
 * A span opens on the local date of a check-in (severity >= 1) when no span is
 * open, and each later check-in in the same span updates lastSeverity and its
 * severity-per-day. A span closes on:
 *   - the local date of a resolution event (severity === 0 or resolvedAt set)  -> resolved
 *   - lastCheckInDate + autoCloseDays, if that date is before today            -> stale
 *   - today                                                                   -> ongoing
 * (D-IE-14). Overlapping/duplicate same-day check-ins collapse into one day.
 *
 * Convention (kept consistent with the calendar's rendering in Task 3.1): the
 * closing day of a RESOLVED span is not itself an active day (the symptom
 * ended before or during that day, not through it) — severityByDay has no
 * entry for a resolution's own date. A span closed by staleness or by "today"
 * (ongoing) DOES include its closing day, since the symptom was still
 * considered active through that day.
 */
export function buildSymptomActivity(
  events: MedicalEvent[],
  timezone: string,
  autoCloseDays: number,
  nowMs?: number
): SymptomActivity {
  const today = localCalendarDate(new Date(nowMs ?? Date.now()).toISOString(), timezone);

  const sorted = [...events]
    .filter((e) => e.eventType === "symptom" || e.eventType === "headache")
    .sort((a, b) => Date.parse(a.occurredAt) - Date.parse(b.occurredAt));

  const spans: SymptomActiveSpan[] = [];
  const severityByDay = new Map<string, number>();

  let checkIns: CheckIn[] = [];
  let peakSeverity = 0;

  const closeSpan = (endDate: string, resolved: boolean, ongoing: boolean, fillThrough: string): void => {
    if (checkIns.length === 0) return;
    fillSeverityByDay(severityByDay, checkIns, fillThrough);
    spans.push({
      startDate: checkIns[0].date,
      endDate,
      peakSeverity,
      lastSeverity: checkIns[checkIns.length - 1].severity,
      ongoing,
      resolved,
    });
    checkIns = [];
    peakSeverity = 0;
  };

  for (const e of sorted) {
    const p = e.payload as Record<string, unknown>;
    const date = localCalendarDate(e.occurredAt, timezone);

    if (isResolutionEvent(e)) {
      if (checkIns.length === 0) continue; // resolution with no open span is ignored
      closeSpan(date, true, false, utcDayWalk(date, -1));
      continue;
    }

    const severity = typeof p.severity === "number" ? p.severity : undefined;
    if (severity === undefined || severity < 1) continue;

    if (checkIns.length === 0) {
      checkIns.push({ date, severity });
      peakSeverity = severity;
      continue;
    }

    const lastCheckIn = checkIns[checkIns.length - 1];

    if (date === lastCheckIn.date) {
      lastCheckIn.severity = severity; // later same-day check-in wins for that day
      peakSeverity = Math.max(peakSeverity, severity);
      continue;
    }

    const horizon = utcDayWalk(lastCheckIn.date, autoCloseDays);
    if (date > horizon) {
      // The open span went stale before this check-in arrived: close it at its
      // horizon, then start a brand-new span from this check-in.
      closeSpan(horizon, false, false, horizon);
      checkIns.push({ date, severity });
      peakSeverity = severity;
      continue;
    }

    checkIns.push({ date, severity });
    peakSeverity = Math.max(peakSeverity, severity);
  }

  if (checkIns.length > 0) {
    const lastCheckIn = checkIns[checkIns.length - 1];
    const horizon = utcDayWalk(lastCheckIn.date, autoCloseDays);
    if (horizon < today) {
      closeSpan(horizon, false, false, horizon); // stale
    } else {
      closeSpan(today, false, true, today); // ongoing
    }
  }

  return { spans, severityByDay };
}

/**
 * Segment symptom/headache events into spans of *literal, contiguous logged
 * days* — unlike `buildSymptomActivity`, this never carries a span forward
 * through days with no check-in (no auto-close horizon, no "ongoing through
 * today"). A span covers a run of calendar days each of which has its own
 * check-in (severity >= 1); any day gap between two check-ins — even one
 * day — ends the span and starts a new one on the next check-in's date.
 *
 * Built for the Condition report's Timeline tab (CONDITION_TRACKING_PLAN.md
 * D-COND-12 originally reused `buildSymptomActivity`'s carried-forward spans
 * there, which reads as one unbroken bar for an unresolved symptom even when
 * the patient only logged it on a couple of days apart — this is the
 * fix for that report). `buildSymptomActivity` itself is unchanged and still
 * backs the Calendar/Stats tabs and follow-up reminders, which intentionally
 * want "still likely active" carry-forward semantics.
 */
export function buildSymptomOccurrenceSpans(
  events: MedicalEvent[],
  timezone: string
): SymptomActiveSpan[] {
  const sorted = [...events]
    .filter((e) => e.eventType === "symptom" || e.eventType === "headache")
    .sort((a, b) => Date.parse(a.occurredAt) - Date.parse(b.occurredAt));

  // Collapse same-day check-ins to one entry (later same-day wins, same as
  // buildSymptomActivity), ignore resolution events and sub-1 severities.
  const byDay = new Map<string, number>();
  for (const e of sorted) {
    const p = e.payload as Record<string, unknown>;
    if (isResolutionEvent(e)) continue;
    const severity = typeof p.severity === "number" ? p.severity : undefined;
    if (severity === undefined || severity < 1) continue;
    byDay.set(localCalendarDate(e.occurredAt, timezone), severity);
  }

  const days = Array.from(byDay.keys()).sort();
  const spans: SymptomActiveSpan[] = [];

  let runStart: string | null = null;
  let runEnd: string | null = null;
  let peakSeverity = 0;

  const flush = () => {
    if (runStart === null || runEnd === null) return;
    spans.push({
      startDate: runStart,
      endDate: runEnd,
      peakSeverity,
      lastSeverity: byDay.get(runEnd) ?? peakSeverity,
      ongoing: false,
      resolved: false,
    });
  };

  for (const d of days) {
    const severity = byDay.get(d)!;
    if (runStart === null) {
      runStart = d;
      runEnd = d;
      peakSeverity = severity;
      continue;
    }
    if (d === utcDayWalk(runEnd!, 1)) {
      runEnd = d;
      peakSeverity = Math.max(peakSeverity, severity);
      continue;
    }
    flush();
    runStart = d;
    runEnd = d;
    peakSeverity = severity;
  }
  flush();

  return spans;
}

/**
 * Daily-resampled severity series for the Chart tab (D-IE-15): one entry per
 * local calendar day in [startDate, endDate] inclusive, `severity` = the active
 * day's carried-forward severity or 0 when not active. Callers map this to
 * DataPoints at local noon so the line sits flat at 0 while resolved and jumps
 * vertically on the onset day.
 */
export function buildSymptomDailySeries(
  activity: SymptomActivity,
  startDate: string,
  endDate: string
): { date: string; severity: number }[] {
  const out: { date: string; severity: number }[] = [];
  let d = startDate;
  while (d <= endDate) {
    out.push({ date: d, severity: activity.severityByDay.get(d) ?? 0 });
    d = utcDayWalk(d, 1);
  }
  return out;
}
